import type { StudioDecomposePlanningMetadata } from './studio-project-plan'
import type { DecomposePlanningProgress } from '@/features/chat/services/app-spec-from-prompt'
import { getLocale } from '@/pages/cogo-marketing/i18n/locale-store'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'
import { PLANNING_EVIDENCE_COPY } from './planning-evidence-copy'

export interface PlanningEvidence {
  status: 'pending' | 'live' | 'fallback'
  title: string
  detail: string
}

/**
 * F-2 — this module renders user-visible text, so it takes the locale.
 *
 * It previously took none at all, and its titles were literals with no home in
 * any copy module. The strings now live in `planning-evidence-copy.ts`; the
 * locale defaults to the store so a caller that forgets the argument still
 * gets the session language rather than a silent English fallback.
 */
export function planningEvidenceFromMetadata(
  metadata: StudioDecomposePlanningMetadata | undefined,
  locale: LocaleCode = getLocale(),
): PlanningEvidence | null {
  if (!metadata) return null
  const copy = PLANNING_EVIDENCE_COPY[locale] ?? PLANNING_EVIDENCE_COPY.en
  if (metadata.status === 'live') {
    // The provider/model/timing receipt decides WHICH sentence is true, not
    // which language it is written in.
    const hasReceipt =
      [
        metadata.provider,
        metadata.model,
        metadata.totalMs !== undefined ? String(metadata.totalMs) : undefined,
        metadata.decomposeWorkerVersion,
      ].filter(Boolean).length > 0
    return {
      status: 'live',
      title: copy.planPrepared,
      detail: hasReceipt ? copy.checkedBrief : copy.preparedFromBrief,
    }
  }
  return {
    status: 'fallback',
    title: copy.needsCloserReview,
    // `fallbackReason` is the producer's own already-localized creator copy
    // (`livePlannerFailureMessage`), so it is used as-is when present.
    detail: metadata.fallbackReason || copy.localPlanReview,
  }
}

export function planningEvidenceFromProgress(
  progress: DecomposePlanningProgress,
  locale: LocaleCode = getLocale(),
): PlanningEvidence | null {
  const copy = PLANNING_EVIDENCE_COPY[locale] ?? PLANNING_EVIDENCE_COPY.en
  if (progress.phase === 'fallback' || progress.phase === 'timeout') {
    return {
      status: 'fallback',
      title:
        progress.phase === 'timeout' ? copy.takingLonger : copy.needsCloserReview,
      detail: progress.message,
    }
  }
  if (
    progress.phase === 'auth' ||
    progress.phase === 'request' ||
    progress.phase === 'response' ||
    progress.phase === 'mapping'
  ) {
    return {
      status: 'pending',
      title: copy.checkingTheBrief,
      detail: progress.message,
    }
  }
  return null
}
